import { useDropzone } from 'react-dropzone';
import { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authHeaders } from '../utils/auth';

function formatBytes(bytes) {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function uploadPhoto(batchId, file, onProgress) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', e => {
            if (e.lengthComputable) onProgress(e.loaded / e.total);
        });
        xhr.addEventListener('load', () => resolve(xhr.status < 400));
        xhr.addEventListener('error', () => reject(new Error('Network error')));
        xhr.open('POST', '/api/photos/upload');
        xhr.setRequestHeader('Authorization', authHeaders().Authorization);
        const formData = new FormData();
        formData.append('photo', file);
        formData.append('batchId', String(batchId));
        xhr.send(formData);
    });
}

const EMPTY_MODAL = {
    open: false,
    done: false,
    cancelled: false,
    total: 0,
    completed: 0,
    failed: [],
    currentFile: null,
    currentPreviewUrl: null,
    currentStage: '',
    currentProgress: 0,
    recentResults: [],
};

export default function Upload() {
    const navigate = useNavigate();
    const { batchId } = useParams();
    const [files, setFiles] = useState([]);
    const [modal, setModal] = useState(EMPTY_MODAL);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const previewUrlRef = useRef(null);
    const cancelledRef = useRef(false);
    const pausePromiseRef = useRef(null);
    const pauseResolveRef = useRef(null);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: { 'image/jpeg': ['.jpg', '.jpeg'] },
        onDrop: (accepted) => setFiles(prev => [...prev, ...accepted]),
    });

    const handleUpload = async () => {
        if (files.length === 0) return alert('Add at least one photo before uploading.');

        const snapshot = [...files];
        setFiles([]);
        cancelledRef.current = false;
        pausePromiseRef.current = null;
        pauseResolveRef.current = null;
        setShowCancelConfirm(false);
        setModal({ ...EMPTY_MODAL, open: true, total: snapshot.length });

        for (const file of snapshot) {
            if (pausePromiseRef.current) await pausePromiseRef.current;
            if (cancelledRef.current) break;

            if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
            const previewUrl = URL.createObjectURL(file);
            previewUrlRef.current = previewUrl;
            setModal(m => ({ ...m, currentFile: { name: file.name, size: file.size }, currentPreviewUrl: previewUrl, currentStage: 'Uploading…', currentProgress: 0 }));

            let ok = false;
            try {
                ok = await uploadPhoto(batchId, file, progress =>
                    setModal(m => ({ ...m, currentProgress: progress }))
                );
            } catch {
                ok = false;
            }

            const resultName = file.name;
            setModal(m => ({
                ...m,
                currentStage: ok ? 'Done ✓' : 'Failed ✗',
                completed: m.completed + 1,
                failed: ok ? m.failed : [...m.failed, resultName],
                recentResults: [{ name: resultName, ok }, ...m.recentResults].slice(0, 5),
            }));

            await new Promise(r => setTimeout(r, 300));
        }

        setModal(m => ({ ...m, done: true, cancelled: cancelledRef.current, currentFile: null, currentStage: '' }));
        setShowCancelConfirm(false);
    };

    const handleCancelClick = () => {
        let resolve;
        pausePromiseRef.current = new Promise(r => { resolve = r; });
        pauseResolveRef.current = resolve;
        setShowCancelConfirm(true);
    };

    const handleCancelConfirm = () => {
        cancelledRef.current = true;
        pausePromiseRef.current = null;
        if (pauseResolveRef.current) pauseResolveRef.current();
        setShowCancelConfirm(false);
    };

    const handleCancelDeny = () => {
        pausePromiseRef.current = null;
        if (pauseResolveRef.current) pauseResolveRef.current();
        setShowCancelConfirm(false);
    };

    const closeModal = () => {
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }
        setModal(EMPTY_MODAL);
    };

    const overallPercent = modal.total > 0 ? Math.round((modal.completed / modal.total) * 100) : 0;
    const bytePercent = Math.round(modal.currentProgress * 100);

    return (
        <div>
            <div className='upload-back-row'>
                <button className='back-link upload-back-link' onClick={() => navigate('/admin/photos')}>
                    ← Batches
                </button>
            </div>
            <div {...getRootProps()} className={isDragActive ? 'dropzone active' : 'dropzone'}>
                <input {...getInputProps()} />
                <p>Drag photos here, or click to select</p>
                <button type='button' className='dropzone-select-btn'>Select Files</button>
            </div>

            {files.length > 0 && (
                <p>{files.length} photo{files.length !== 1 ? 's' : ''} ready to upload</p>
            )}

            {files.length > 0 && (
                <button className='dropzone-btn upload-btn' onClick={handleUpload}>
                    Upload {files.length} photo{files.length !== 1 ? 's' : ''}
                </button>
            )}

            {modal.open && (
                <div className='ai-modal-backdrop' role='dialog' aria-modal='true' aria-label='Upload progress'>
                    <div className='ai-modal'>
                        <div className='ai-modal-header'>
                            <h2 className='ai-modal-title'>
                                <span aria-hidden='true'>⬆</span> Uploading Photos
                            </h2>
                            {modal.done && (
                                <button className='ai-modal-close' onClick={closeModal} aria-label='Close'>×</button>
                            )}
                        </div>

                        {modal.currentFile && (
                            <div className='ai-modal-current'>
                                <div className='ai-modal-thumb'>
                                    <img src={modal.currentPreviewUrl} alt='' />
                                </div>
                                <div className='ai-modal-photo-info'>
                                    <span className='ai-modal-filename'>{modal.currentFile.name}</span>
                                    <span className='upload-modal-filesize'>{formatBytes(modal.currentFile.size)}</span>
                                                    <span className={`ai-modal-stage${modal.currentStage.startsWith('Done') ? ' done' : modal.currentStage.startsWith('Failed') ? ' upload-failed' : ''}`}>
                                        {modal.currentStage}
                                    </span>
                                    {modal.currentStage === 'Uploading…' && (
                                        <div className='upload-modal-byte-progress'>
                                            <div className='ai-modal-progress-bar-track'>
                                                <div className='ai-modal-progress-bar-fill' style={{ width: `${bytePercent}%` }} />
                                            </div>
                                            <span className='upload-modal-byte-pct'>{bytePercent}%</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div>
                            <div className='ai-modal-progress-row'>
                                <span className='ai-modal-count'>
                                    {modal.completed} of {modal.total} {modal.done ? (modal.cancelled ? 'uploaded — cancelled' : 'complete') : 'files'}
                                </span>
                            </div>
                            <div className='ai-modal-progress-bar-track upload-modal-overall-bar'>
                                <div className='ai-modal-progress-bar-fill' style={{ width: `${overallPercent}%` }} />
                            </div>
                        </div>

                        {modal.recentResults.length > 0 && (
                            <div className='upload-modal-results'>
                                {modal.recentResults.map((r, i) => (
                                    <span key={i} className={`upload-modal-result-chip${r.ok ? '' : ' failed'}`}>
                                        {r.ok ? '✓' : '✗'} {r.name}
                                    </span>
                                ))}
                            </div>
                        )}

                        {modal.done && (
                            <>
                                <div className='ai-modal-summary'>
                                    <div className='ai-modal-summary-item'>
                                        <span className='ai-summary-value'>{modal.completed - modal.failed.length}</span>
                                        <span className='ai-summary-label'>Uploaded</span>
                                    </div>
                                    {modal.failed.length > 0 && (
                                        <div className='ai-modal-summary-item'>
                                            <span className='ai-summary-value upload-modal-fail-count'>{modal.failed.length}</span>
                                            <span className='ai-summary-label'>Failed</span>
                                        </div>
                                    )}
                                </div>
                                {modal.failed.length > 0 && (
                                    <div>
                                        <p className='upload-fail-heading'>{modal.failed.length} upload{modal.failed.length !== 1 ? 's' : ''} failed:</p>
                                        <ul className='upload-fail-list'>
                                            {modal.failed.map(name => <li key={name}>{name}</li>)}
                                        </ul>
                                    </div>
                                )}
                            </>
                        )}

                        {showCancelConfirm && (
                            <div className='ai-cancel-confirm'>
                                <p>Stop uploading? Photos uploaded so far will be kept.</p>
                                <div className='ai-cancel-confirm-btns'>
                                    <button className='ai-cancel-deny-btn' onClick={handleCancelDeny}>Keep going</button>
                                    <button className='ai-cancel-confirm-btn' onClick={handleCancelConfirm}>Stop</button>
                                </div>
                            </div>
                        )}

                        <div className='ai-modal-footer'>
                            {!modal.done && !showCancelConfirm && (
                                <button className='ai-modal-cancel-btn' onClick={handleCancelClick}>Cancel</button>
                            )}
                            {modal.done && (
                                <button className='ai-modal-done-btn' onClick={closeModal}>Done</button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
